import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'

const Root = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;

  ${mediaQueryUp.desktop} {
    position: static;
    display: block;
    max-width: ${rem(360)};
  }
`

const Panel = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  width: 100%;
  max-width: ${rem(360)};
  padding: ${rem(32)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: ${props => props.theme.borderRadius.M};
  position: relative;
  height: 100%;
`

const Layout = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to bottom,transparent 50%,white 50%);

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const Grid = styled.div`
  display: grid;
  width: 100%;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  margin-top: ${rem(8)};

  & > * {
    overflow: hidden;
  }

  ${mediaQueryUp.desktop} {
    margin-top: 0;
  }
`

const DisplayValue = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(16)};
  margin-top: ${rem(16)};

  ${mediaQueryUp.desktop} {
    margin-top: 0;
  }
`

interface Props {
  referralLogTotal: App.ReferralLogTotal
}

function ReferralPanel({ referralLogTotal }: Props) {
  const intl = useIntl()

  const trackYourLabel = intl.formatMessage({
    id: '2ocHX1EOkIBKcqsydQt3',
    defaultMessage: 'Track your',
    description: 'Intro text above referrals & rewards heading on referral panel',
  })
  const referralsHeading = intl.formatMessage({
    id: 'gDuCcMfLpgO2Z5-fkt44',
    defaultMessage: 'Referrals & rewards',
    description: 'Section heading for the referral tracking panel on the marketing page',
  })
  const pendingLabel = intl.formatMessage({
    id: 'd4ZI21NZocxwYZYvughZ',
    defaultMessage: 'Pending',
    description: 'Label for pending referrals count on referral panel',
  })
  const availableLabel = intl.formatMessage({
    id: 'XcL1zTTp6TUPy0a9lWn0',
    defaultMessage: 'Available',
    description: 'Label for available referrals count on referral panel',
  })
  const redeemedLabel = intl.formatMessage({
    id: 'L2_BqpzSM1I81B9kmfMY',
    defaultMessage: 'Redeemed',
    description: 'Label for redeemed referrals count on referral panel',
  })
  const creditsLabel = intl.formatMessage({
    id: '9Yy46SjqhX0qVRo34sKo',
    defaultMessage: 'Luxury Escapes Credits',
    description: 'Label for total credits earned from referrals on referral panel',
  })

  return (
    <Root>
      <Layout />
      <Panel>
        <BodyText variant="large">{trackYourLabel}</BodyText>
        <Heading variant="heading3">{referralsHeading}</Heading>
        <Grid>
          <div>
            <Heading variant="heading3">{referralLogTotal.pending ?? 0}</Heading>
            <BodyText variant="large">{pendingLabel}</BodyText>
          </div>
          <div>
            <Heading variant="heading3">{referralLogTotal.available_referrals ?? 0}</Heading>
            <BodyText variant="large">{availableLabel}</BodyText>
          </div>
          <div>
            <Heading variant="heading3">{referralLogTotal.total_amount_redeemed ?? 0}</Heading>
            <BodyText variant="large">{redeemedLabel}</BodyText>
          </div>
        </Grid>
        <DisplayValue>
          <BodyText variant="medium">{creditsLabel}</BodyText>
          <Heading variant="heading3"><FormatCurrency value={referralLogTotal.creditsRedeemed || 0} format="dynamicDollar" /></Heading>
        </DisplayValue>
      </Panel>
    </Root>
  )
}

export default ReferralPanel
